import { Button, Flex, Input, Table, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useState } from 'react';

import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { Schemas } from '@api-client/generated/types';

type ObligationFile = Schemas.AdminObligationFileResponseDto;

type ObligationFilesPanelProps = {
  files: ObligationFile[];
  onPreview: (file: ObligationFile) => void;
  onRename: (fileId: string, name: string) => Promise<void>;
  onDelete: (file: ObligationFile) => void;
};

const getApprovedByString = (events: ObligationFile['events']) => {
  // Mirror TaxTable's latest-event logic: pick the most recent approval,
  // not the first match (API order isn't guaranteed).
  const event = [...(events ?? [])]
    .filter((item) => item.type === 'approved')
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )[0];
  if (event) {
    const date = dayjs(event.createdAt).format('DD/MM/YYYY HH:mm');
    return `${event.user.fullName} at ${date}`;
  }
  return null;
};

const ObligationFilesPanel = (props: ObligationFilesPanelProps) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [savingId, setSavingId] = useState<string | null>(null);

  const startEditing = (file: ObligationFile) => {
    setEditingId(file.id);
    setDraftName(file.name || file.fileName || '');
  };

  const cancelEditing = () => {
    setEditingId(null);
    setDraftName('');
  };

  const saveEditing = async (fileId: string) => {
    setSavingId(fileId);
    try {
      await props.onRename(fileId, draftName);
      setEditingId(null);
      setDraftName('');
    } finally {
      setSavingId(null);
    }
  };

  const columns: ColumnsType<ObligationFile> = [
    {
      title: 'Name',
      key: 'name',
      render: (_, file) => {
        if (editingId === file.id) {
          return (
            <Input
              autoFocus
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              onPressEnter={() => saveEditing(file.id)}
              style={{ maxWidth: 360 }}
            />
          );
        }

        return (
          <Typography.Link onClick={() => props.onPreview(file)}>
            {file.name || file.fileName}
          </Typography.Link>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 320,
      render: (_, file) =>
        file.status === 'approved' ? (
          <Typography.Text>
            <Typography.Text type="success" strong>
              Approved
            </Typography.Text>{' '}
            {getApprovedByString(file.events)}
          </Typography.Text>
        ) : (
          <Typography.Text type="secondary">
            Waiting for approval
          </Typography.Text>
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      render: (_, file) => {
        if (editingId === file.id) {
          return (
            <Flex gap={8} justify="flex-end">
              <Button
                type="primary"
                aria-label="Save file name"
                icon={<CheckOutlined />}
                loading={savingId === file.id}
                onClick={() => saveEditing(file.id)}
              />
              <Button
                type="secondary"
                aria-label="Cancel renaming"
                icon={<CloseOutlined />}
                disabled={savingId === file.id}
                onClick={cancelEditing}
              />
            </Flex>
          );
        }

        return (
          <Flex gap={8} justify="flex-end">
            <Button
              type="secondary"
              aria-label={`Rename ${file.name || file.fileName}`}
              icon={<EditOutlined />}
              onClick={() => startEditing(file)}
            />
            <Button
              danger
              type="secondary"
              aria-label={`Delete ${file.name || file.fileName}`}
              icon={<DeleteOutlined />}
              onClick={() => props.onDelete(file)}
            />
          </Flex>
        );
      },
    },
  ];

  return (
    <Table
      dataSource={props.files}
      columns={columns}
      rowKey="id"
      pagination={false}
      size="small"
    />
  );
};

export default ObligationFilesPanel;
